import * as React from "react";
import { useState, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  TextField,
  Card,
  IconButton,
  CardHeader,
  CardContent,
  Box,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Grid2";
import { PhotoCamera, Delete } from "@mui/icons-material";
import {
  Prefixes,
  Genders,
  BloodGroups,
  NationalIdTypes,
  BloodGroupOptions,
} from "../Utility/Constants";
import { updatePatient } from "../API/PatientsApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  calculateAge,
  calculateAgeOnlyYear,
  showLoadingImage,
} from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css";
import GenericTextField from "../common/GenericTextField";
import GenericDropDown from "../common/GenericDropDown";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
 
const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed"];
const genderOptions = ["Male", "Female", "Other"];
const prefixOptions = ["Mr.", "Ms.", "Dr.", "Prof."];
 
function EditPatient({ patient, onSave }) {
  const theme = useTheme();
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [editedPatient, setEditedPatient] = useState(patient);
  const [ageInYears, setAgeInYears] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // State for phone validation errors
  const [phoneValidationError, setPhoneValidationError] = useState(false);
 
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
 
  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  // Function to validate phone numbers
  const validatePhoneNumbers = (mobileNo, emergencyPhone) => {
    if (mobileNo && emergencyPhone && mobileNo.trim() === emergencyPhone.trim()) {
      return false;
    }
    return true;
  };
 
  const handleChange = (e) => {
    const { name, value } = e.target;
 
    if (name === "ageInYears") {
      const age = parseInt(value, 10);
      if (!isNaN(age)) {
        const today = new Date();
        const birthDate = new Date(
          today.getFullYear() - age,
          today.getMonth(),
          today.getDate()
        );
        const dobStr = birthDate.toISOString().split("T")[0];
 
        return setEditedPatient((prev) => ({
          ...prev,
          ageInYears: value,
          dateOfBirth: dobStr,
        }));
      }
    }

    // Check phone validation when mobile number or emergency contact changes
    if (name === "mobileNo" || name === "emergencyContactPhoneNumber") {
      const newPatient = { ...editedPatient, [name]: value };
      const isValid = validatePhoneNumbers(
        name === "mobileNo" ? value : editedPatient.mobileNo,
        name === "emergencyContactPhoneNumber" ? value : editedPatient.emergencyContactPhoneNumber
      );
      setPhoneValidationError(!isValid);
    }
 
    setEditedPatient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
 
  const handleDoBChange = (value) => {
    setEditedPatient((prev) => ({
      ...prev,
      ["dateOfBirth"]: value,
      ["ageInYears"]: calculateAgeOnlyYear(value),
    }));
  };
 
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
 
    const fileSizeLimit = 1 * 1024 * 1024;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
 
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, PNG, and GIF files are allowed.");
      return;
    }
 
    if (file.size > fileSizeLimit) {
      alert("File size must be less than 1MB.");
      return;
    }
 
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditedPatient((prev) => ({
        ...prev,
        photo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };
 
  const handlePhotoDelete = () => {
    setEditedPatient((prev) => ({
      ...prev,
      photo: "",
    }));
  };
 
  const handleSave = async () => {
    const requiredFields = [
      {
        field: "firstName",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "prefix",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "dateOfBirth",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "mobileNo",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "gender",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "lastName",
        message: "Filling in all mandatory fields is required",
      },
    ];
    setIsSubmitted(true);
 
    // Check for missing required fields
    for (const { field, message } of requiredFields) {
      if (!editedPatient[field]) {
        showSnackbar(message, "error");
        return;
      }
    }

    // Validate phone numbers are not the same
    const isPhoneValid = validatePhoneNumbers(
      editedPatient.mobileNo,
      editedPatient.emergencyContactPhoneNumber
    );

    if (!isPhoneValid) {
      setPhoneValidationError(true);
      showSnackbar("Patient contact number and emergency contact number cannot be the same", "error");
      return;
    }
 
    setIsSaving(true);
 
    try {
      const result = await updatePatient(auth, editedPatient.id, editedPatient);
 
      if (result.isSuccess) {
        showSnackbar("Patient details updated successfully.", "success");
        onSave(editedPatient);
      } else {
        showSnackbar("Failed to update patient details.", "error");
      }
    } catch (error) {
      showSnackbar(
        "An error occurred while updating patient details.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };
 
  return (
    <Box sx={{ position: "relative" }}>
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <Grid2 size={12} container direction="column" spacing={1}>
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showSave={true}
              onSave={handleSave}
              disabled={isLoading || isSaving}
            />
          </Grid2>
        </Grid2>
        <Grid2
          size={12}
          container
          spacing={1}
          sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
        >
          <Grid2
            size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
            container
            spacing={2}
          >
            <Grid2>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{
                  padding: "10px",
                  width: "100%",
                  pointerEvents: isSaving || isLoading ? "none" : "auto",
                  opacity: isSaving || isLoading ? 0.6 : 1,
                }}
              >
                <CardHeader
                  subheader="Patient's Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={12} container alignItems="center" spacing={2}>
                    <Grid2
                      size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                    >
                      <GenericDropDown
                        label="Prefix"
                        value={editedPatient.prefix || ""}
                        onChange={handleChange}
                        options={Prefixes.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        required={true}
                        error={isSubmitted && !editedPatient.prefix}
                        name="prefix"
                        size="small"
                        InputLabelProps={{
                          sx: {
                            top: "-7px",
                          },
                        }}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [editedPatient.prefix], theme)
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                      <GenericTextField
                        variant="outlined"
                        label="Suffix"
                        name="suffix"
                        size="small"
                        fullWidth
                        value={editedPatient.suffix || ""}
                        onChange={handleChange}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 size={12} container alignItems="center" spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                      <GenericTextField
                        variant="outlined"
                        label="First Name"
                        name="firstName"
                        size="small"
                        fullWidth
                        value={editedPatient.firstName || ""}
                        onChange={handleChange}
                        required
                        error={isSubmitted && !editedPatient.firstName}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                      <GenericTextField
                        variant="outlined"
                        label="Last Name"
                        name="lastName"
                        fullWidth
                        size="small"
                        value={editedPatient.lastName || ""}
                        onChange={handleChange}
                        required
                        error={isSubmitted && !editedPatient.lastName}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red",
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>
 
                  <Grid2 container size={12} spacing={2} direction="column">
                    <Grid2 container size={12} spacing={2}>
                      <Grid2
                        size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                        backgroundColor="white"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <GenericDatePicker
                            label="Date of Birth"
                            value={editedPatient?.dateOfBirth ? dayjs(editedPatient.dateOfBirth) : null}
                            onChange={(value) => {
                              setEditedPatient((prev) => ({
                                ...prev,
                                dateOfBirth: value ? value.format("YYYY-MM-DD") : "",
                                ageInYears: value ? calculateAgeOnlyYear(value.format("YYYY-MM-DD")) : "",
                              }));
                            }}
                            maxDate={dayjs()}
                            returnDayjsObject={true}
                            required={true}
                            error={isSubmitted && !editedPatient?.dateOfBirth}
                            displayFormat="DD-MMM-YYYY"
                          />
                        </LocalizationProvider>
                      </Grid2>
 
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                        <GenericTextField
                          label="Age"
                          name="ageInYears"
                          fullWidth
                          size="small"
                          onChange={handleChange}
                          type="number"
                          value={editedPatient.ageInYears}
                          slotProps={{
                            inputLabel: { shrink: true },
                          }}
                        />
                      </Grid2>
                    </Grid2>
 
                    <Grid2 container size={12}>
                      <Grid2
                        size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                        backgroundColor="white"
                      >
                        <TextField
                          margin="dense"
                          size="small"
                          label="Email"
                          name="emailId"
                          fullWidth
                          value={editedPatient.emailId}
                          onChange={handleChange}
                        />
                      </Grid2>
 
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                        <GenericTextField
                          label="Phone"
                          name="mobileNo"
                          fullWidth
                          size="small"
                          value={editedPatient.mobileNo || ""}
                          onChange={handleChange}
                          required
                          error={(isSubmitted && !editedPatient.mobileNo) || phoneValidationError}
                          helperText={phoneValidationError ? "Cannot be same as emergency contact" : ""}
                          sx={{
                            "& .MuiFormLabel-asterisk": {
                              color: "red",
                            },
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
 
                  <Grid2 size={12} container alignItems="center" spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Marital Status"
                        value={editedPatient.maritalStatus || ""}
                        onChange={handleChange}
                        options={maritalStatusOptions.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        InputLabelProps={{
                          sx: {
                            top: "-7px",
                          },
                        }}
                        required={false}
                        error={false}
                        name="maritalStatus"
                        size="small"
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(
                            option,
                            [editedPatient.maritalStatus],
                            theme
                          )
                        }
                      />
                    </Grid2>
 
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Blood Group"
                        value={editedPatient.bloodGroup || ""}
                        onChange={handleChange}
                        options={BloodGroupOptions.map((option) => ({
                          value: option.value,
                          label: option.displayValue,
                        }))}
                        InputLabelProps={{
                          sx: {
                            top: "-7px",
                          },
                        }}
                        required={false}
                        error={false}
                        name="bloodGroup"
                        size="small"
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [editedPatient.bloodGroup], theme)
                        }
                      />
                    </Grid2>
 
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                      <GenericDropDown
                        label="Gender"
                        value={editedPatient.gender || ""}
                        onChange={handleChange}
                        options={Genders.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        InputLabelProps={{
                          sx: {
                            top: "-7px",
                          },
                        }}
                        required={false}
                        error={false}
                        name="gender"
                        size="small"
                        MenuProps={MenuProps}
                        getStyles={(option) =>
                          getStyles(option, [editedPatient.gender], theme)
                        }
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
 
            {/* Address Details Section */}
            <Grid2>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{
                  padding: "10px",
                  width: "100%",
                  pointerEvents: isSaving || isLoading ? "none" : "auto",
                  opacity: isSaving || isLoading ? 0.6 : 1,
                }}
              >
                <CardHeader
                  subheader="Address Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      margin="dense"
                      size="small"
                      label="House No"
                      name="addressHouseNo"
                      fullWidth
                      value={editedPatient.addressHouseNo}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      margin="dense"
                      size="small"
                      label="Street"
                      name="addressStreet"
                      fullWidth
                      value={editedPatient.addressStreet}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="City"
                      name="addressCity"
                      fullWidth
                      size="small"
                      value={editedPatient.addressCity}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="State"
                      name="addressState"
                      fullWidth
                      size="small"
                      value={editedPatient.addressState}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="Country"
                      name="addressCountry"
                      fullWidth
                      size="small"
                      value={editedPatient.addressCountry}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="Pin Code"
                      name="addressPIN"
                      fullWidth
                      size="small"
                      value={editedPatient.addressPIN}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
            
            {/* National ID Proof Section */}
            <Grid2 size={12}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{
                  padding: "10px",
                  width: "100%",
                  pointerEvents: isSaving || isLoading ? "none" : "auto",
                  opacity: isSaving || isLoading ? 0.6 : 1,
                }}
              >
                <CardHeader
                  subheader="National ID "
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericDropDown
                      label="National ID Type"
                      value={editedPatient.nationalIdType || ""}
                      onChange={handleChange}
                      options={NationalIdTypes.map((option) => ({
                        value: option,
                        label: option,
                      }))}
                      InputLabelProps={{
                        sx: {
                          top: "-7px",
                        },
                      }}
                      required={false}
                      error={false}
                      name="nationalIdType"
                      size="small"
                    />
                  </Grid2>
 
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                    <GenericTextField
                      label="ID Number"
                      name="nationalId"
                      fullWidth
                      size="small"
                      value={editedPatient.nationalId}
                      onChange={handleChange}
                      slot={{
                        inputLabel: { shrink: editedPatient.nationalId },
                      }}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
 
            {/* Emergency Contact Details Section */}
            <Grid2 size={12}>
              <Card
                className="outerCard"
                variant="outlined"
                sx={{
                  padding: "10px",
                  width: "100%",
                  pointerEvents: isSaving || isLoading ? "none" : "auto",
                  opacity: isSaving || isLoading ? 0.6 : 1,
                }}
              >
                <CardHeader
                  subheader="Emergency Contact Details"
                  sx={{ paddingX: 0 }}
                ></CardHeader>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericTextField
                      variant="outlined"
                      label="Name"
                      name="emergencyContactPerson"
                      fullWidth
                      size="small"
                      value={editedPatient.emergencyContactPerson}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericTextField
                      label="Phone"
                      name="emergencyContactPhoneNumber"
                      fullWidth
                      size="small"
                      value={editedPatient.emergencyContactPhoneNumber}
                      onChange={handleChange}
                      error={phoneValidationError}
                      helperText={phoneValidationError ? "Cannot be same as patient contact" : ""}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                    <GenericTextField
                      label="Relationship"
                      name="emergencyContactRelationship"
                      fullWidth
                      size="small"
                      value={editedPatient.emergencyContactRelationship}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
          </Grid2>
 
          {/* Image Upload Section */}
          <Grid2
            size={{ xs: 8, sm: 8, md: 8, lg: 2, xl: 2 }}
            container
            alignItems="flex-start"
            justifyContent="flex-start"
            direction="column"
          >
            <Card
              className="outerCard"
              variant="outlined"
              sx={{
                padding: "10px",
                width: "100%",
                pointerEvents: isSaving || isLoading ? "none" : "auto",
                opacity: isSaving || isLoading ? 0.6 : 1,
              }}
            >
              <CardHeader subheader="Upload Image" sx={{ paddingX: 0 }} />
              <CardContent>
                <Grid2
                  container
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                >
                  <Grid2 size={12} container justifyContent="center">
                    <img
                      src={
                        editedPatient.photo ||
                        "./placeholder-image-male-gender.webp"
                      }
                      alt="PatientPhoto"
                      width="120"
                      height="120"
                      className="border rounded p-0"
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ padding: 0 }}
                    >
                      Upload image size max 1MB
                    </Typography>
                  </Grid2>
 
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif"
                    hidden
                    id="photo-upload"
                    onChange={handlePhotoUpload}
                  />
 
                  <Grid2
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <Grid2>
                      <label htmlFor="photo-upload">
                        <IconButton component="span">
                          <PhotoCamera />
                        </IconButton>
                      </label>
                    </Grid2>
 
                    {editedPatient.photo && (
                      <Grid2>
                        <IconButton onClick={handlePhotoDelete}>
                          <Delete />
                        </IconButton>
                      </Grid2>
                    )}
                  </Grid2>
                </Grid2>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}
 
export default EditPatient;